import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  User, Bell, Shield, Palette, CreditCard, Users, Key, Save, Moon, Sun, Smartphone,
  Check, Globe, Mail, Zap, Brain, MessageSquare, Volume2, Clock, Tag, TestTube,
  Webhook, Link2, Copy, Eye, Trash2, Plus, AlertTriangle, RefreshCw,
  ChevronRight, Info, Monitor, LogOut, Megaphone, Loader2,
  Settings as SettingsIcon, Sliders, Send, ShieldCheck, BarChart3, Inbox
} from 'lucide-react'
import api from '../../lib/api'
import { isPushSupported, getPushState, enablePush, disablePush } from '../../lib/push'
import { setAudioEnabled as setAudioOnGlobally, playChime } from '../../lib/notificationSound'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import '../Pages.css'
import './Settings.css'

const BILLING_PLANS = [
  {
    id: 'free', name: 'Free', tagline: 'Try Synxreach, no card needed',
    price: 0, color: '#64748b',
    limits: { contacts: 500, contactUploads: 500, emails: 500, aiEmails: 25 },
    features: [
      '500 contacts',
      '500 emails/mo',
      'BounceShield Basic',
      '25 AI emails/mo',
      'Single inbox view',
      'Community support',
    ],
  },
  {
    id: 'starter', name: 'Starter', tagline: 'Solo founders, testing',
    price: 29, color: '#6366f1',
    limits: { contacts: 10000, contactUploads: 10000, emails: 10000, aiEmails: 500 },
    features: [
      '10,000 contacts',
      '10,000 emails/mo',
      'BounceShield included',
      '500 AI emails/mo',
      'Basic inbox (single view)',
      'Built-in SMTP relay, basic campaigns',
    ],
  },
  {
    id: 'growth', name: 'Growth', tagline: 'Growing teams, serious outreach',
    price: 59, color: '#22c55e', recommended: true,
    limits: { contacts: 50000, contactUploads: 50000, emails: 100000, aiEmails: 5000 },
    features: [
      '50,000 contacts',
      '100,000 emails/mo',
      'BounceShield Pro',
      '5,000 AI emails/mo + AI reply suggestions',
      'Full ReplyHub (unified inbox, labels, filters)',
      'A/B testing, API access, team seats',
    ],
  },
  {
    id: 'scale', name: 'Scale', tagline: 'Agencies & power users',
    price: 99, color: '#f59e0b',
    limits: { contacts: null, contactUploads: null, emails: 500000, aiEmails: 25000 },
    features: [
      'Unlimited contacts',
      '500,000 emails/mo',
      'BounceShield Elite',
      '25,000 AI emails/mo + AI auto-categorization',
      'ReplyHub Pro (auto-sort, sentiment, priority queue)',
      'White-label, priority support, custom domains',
    ],
  },
]

// Server plans (`GET /billing/plans`) are the source of truth for price and
// limits; BILLING_PLANS above only supplies cosmetics (color, tagline, badge)
// and acts as the fallback when the endpoint is unavailable.
const normalizeServerPlan = (raw) => {
  const meta = BILLING_PLANS.find(p => p.id === raw.id) || {}
  const limits = raw.limits || {}
  const fmtLimit = (n) => (n == null ? 'Unlimited' : Number(n).toLocaleString('en-IN'))
  const derivedFeatures = [
    `${fmtLimit(limits.contacts)} contacts`,
    `${fmtLimit(limits.emails)} emails/mo`,
    `${fmtLimit(limits.ai_emails)} AI emails/mo`,
    limits.ai_credits != null ? `${fmtLimit(limits.ai_credits)} AI credits/mo` : null,
  ].filter(Boolean)
  const serverFeatures = Array.isArray(raw.features) ? raw.features : null

  return {
    ...meta,
    id: raw.id,
    name: raw.name || meta.name || raw.id,
    tagline: raw.tagline || meta.tagline || '',
    price: (raw.price_cents ?? 0) / 100,
    currency: raw.currency || 'INR',
    color: meta.color || '#6366f1',
    recommended: raw.recommended ?? meta.recommended ?? false,
    features: (serverFeatures && serverFeatures.length)
      ? serverFeatures
      : (meta.features || derivedFeatures),
  }
}

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js'

// All pricing is in INR — render the rupee symbol, not a dollar sign.
const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£' }
const currencySymbol = (code) => CURRENCY_SYMBOLS[(code || 'INR').toUpperCase()] || `${(code || '').toUpperCase()} `
const formatMoney = (amount, currency = 'INR', opts = {}) =>
  `${currencySymbol(currency)}${Number(amount || 0).toLocaleString('en-IN', opts)}`

const loadRazorpay = () => new Promise((resolve) => {
  if (typeof window === 'undefined') return resolve(false)
  if (window.Razorpay) return resolve(true)
  const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`)
  if (existing) {
    existing.addEventListener('load', () => resolve(true))
    existing.addEventListener('error', () => resolve(false))
    return
  }
  const s = document.createElement('script')
  s.src = RAZORPAY_SCRIPT_URL
  s.onload = () => resolve(true)
  s.onerror = () => resolve(false)
  document.body.appendChild(s)
})

const settingsSections = [
  { id: 'account', icon: User, label: 'Account' },
  { id: 'preferences', icon: Sliders, label: 'Preferences' },
  { id: 'email-sending', icon: Send, label: 'Email Sending' },
  { id: 'notifications', icon: Bell, label: 'Notifications' },
  { id: 'security', icon: Shield, label: 'Security' },
  { id: 'workspace', icon: Users, label: 'Workspace & Team' },
  { id: 'integrations', icon: Key, label: 'Integrations' },
  { id: 'billing', icon: CreditCard, label: 'Billing' },
  { id: 'appearance', icon: Palette, label: 'Appearance' },
]

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
]

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh', label: 'Chinese' },
]

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <label className={`st-toggle ${disabled ? 'disabled' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="st-toggle-track">
        <span className="st-toggle-thumb" />
      </span>
    </label>
  )
}

function SettingRow({ icon: Icon, label, description, children, badge }) {
  return (
    <div className="st-row">
      <div className="st-row-left">
        {Icon && <div className="st-row-icon"><Icon size={18} /></div>}
        <div className="st-row-info">
          <span className="st-row-label">
            {label}
            {badge && <span className={`st-badge st-badge--${badge.type || 'default'}`}>{badge.text}</span>}
          </span>
          {description && <span className="st-row-desc">{description}</span>}
        </div>
      </div>
      <div className="st-row-right">{children}</div>
    </div>
  )
}

function Card({ title, description, children, action, variant }) {
  return (
    <div className={`st-card ${variant ? `st-card--${variant}` : ''}`}>
      <div className="st-card-head">
        <div className="st-card-head-text">
          <h4>{title}</h4>
          {description && <p>{description}</p>}
        </div>
        {action && <div className="st-card-head-action">{action}</div>}
      </div>
      <div className="st-card-body">{children}</div>
    </div>
  )
}

function Settings() {
  const { user, updateUser } = useAuth()
  const { theme, setTheme, accentColor, setAccentColor, density, setDensity } = useTheme()
  const [searchParams] = useSearchParams()
  const [activeSection, setActiveSection] = useState(() => searchParams.get('section') || 'account')

  // Deep-link support, e.g. links to /settings?section=billing (the AI
  // credits "out of credits" modal upgrade link).
  useEffect(() => {
    const section = searchParams.get('section')
    if (section) setActiveSection(section)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [teamLoading, setTeamLoading] = useState(false)
  const [teamError, setTeamError] = useState(null)
  const [inviteLoading, setInviteLoading] = useState(false)

  const [profile, setProfile] = useState({
    firstName: '', lastName: '', email: '',
    company: '', timezone: 'America/New_York', language: 'en'
  })

  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' })
  const [passwordMsg, setPasswordMsg] = useState(null)
  const [passwordSaving, setPasswordSaving] = useState(false)

  // Populate profile from auth user on mount
  useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').split(' ')
      setProfile(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        company: user.company || '',
        timezone: user.timezone || 'America/New_York',
        language: user.language || 'en',
      }))
    }
  }, [user])

  // Fetch full profile from server
  const fetchProfile = useCallback(async () => {
    setProfileLoading(true)
    try {
      const res = await api.get('/auth/me')
      const me = res.data
      const nameParts = (me.name || me.username || '').split(' ')
      setProfile({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: me.email || '',
        company: me.company || '',
        timezone: me.timezone || 'America/New_York',
        language: me.language || 'en',
      })
    } catch (err) {
      console.error('Failed to load profile:', err)
    } finally {
      setProfileLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const [preferences, setPreferences] = useState({
    autoTagLeadStatus: true, oooDetection: true, aiSuggestedReplies: true,
    aiInboxManager: false, viewBounceInUnibox: true, autoPauseHighBounce: true,
    singleAccountPerGap: false, aiSmartSending: true, plainTextFirstEmail: false,
  })

  const [aiLabels, setAiLabels] = useState([])
  const [aiLabelsLoading, setAiLabelsLoading] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')

  const fetchAiLabels = useCallback(async () => {
    setAiLabelsLoading(true)
    try {
      const res = await api.get('/api/v1/settings/ai-labels')
      setAiLabels(res.data?.labels || [])
    } catch (err) {
      console.error('Failed to load AI labels:', err)
    } finally {
      setAiLabelsLoading(false)
    }
  }, [])

  const [sendingSettings, setSendingSettings] = useState({
    campaignLimitResetTimezone: 'America/New_York', minTimeGap: 9, randomDelay: 5,
    dailySendingLimit: 50, openTracking: true, linkTracking: true,
    unsubscribeHeader: true, stopOnReply: true, campaignSlowRamp: true, espMatching: false,
  })

  const [notifications, setNotifications] = useState({
    campaignCompleted: true, newReplies: true, weeklyReport: true, bounceAlerts: true,
    positiveReply: true, dailyDigest: false, teamActivity: false, systemUpdates: true,
    notificationRecipient: 'owner', audioEnabled: false,
    emailNotifications: true, browserNotifications: true, securityAlerts: true,
    productUpdates: false, monthlyReport: true,
  })
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [notificationsSaving, setNotificationsSaving] = useState(false)

  const [pushSupported, setPushSupported] = useState(false)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushPermission, setPushPermission] = useState('default')

  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)

  // 2FA state
  const [twoFaEnabled, setTwoFaEnabled] = useState(false)
  const [twoFaSetup, setTwoFaSetup] = useState(null) // { secret, otpauth_uri, qr_data_uri }
  const [twoFaCode, setTwoFaCode] = useState('')
  const [twoFaBusy, setTwoFaBusy] = useState(false)
  const [twoFaError, setTwoFaError] = useState('')
  const [backupCodes, setBackupCodes] = useState(null) // string[] shown once after verify

  // For Disable / Regenerate
  const [twoFaPassword, setTwoFaPassword] = useState('')
  const [twoFaMode, setTwoFaMode] = useState(null) // null | 'setup' | 'disable' | 'regenerate'

  const fetchTwoFaStatus = useCallback(async () => {
    try {
      const res = await api.get('/auth/2fa/status')
      setTwoFaEnabled(!!res.data?.enabled)
    } catch (e) { /* ignore */ }
  }, [])

  const handleStartSetup = async () => {
    setTwoFaError('')
    setTwoFaBusy(true)
    try {
      const res = await api.post('/auth/2fa/setup')
      setTwoFaSetup(res.data)
      setTwoFaMode('setup')
      setTwoFaCode('')
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || 'Failed to start 2FA setup')
    } finally {
      setTwoFaBusy(false)
    }
  }

  const handleVerifySetup = async () => {
    if (!twoFaCode.trim()) return
    setTwoFaError('')
    setTwoFaBusy(true)
    try {
      const res = await api.post('/auth/2fa/verify-setup', { code: twoFaCode.trim() })
      setBackupCodes(res.data?.backup_codes || [])
      setTwoFaEnabled(true)
      setTwoFaSetup(null)
      setTwoFaMode(null)
      setTwoFaCode('')
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || 'Invalid code')
    } finally {
      setTwoFaBusy(false)
    }
  }

  const handleDisable = async () => {
    setTwoFaError('')
    setTwoFaBusy(true)
    try {
      await api.post('/auth/2fa/disable', { current_password: twoFaPassword, code: twoFaCode.trim() })
      setTwoFaEnabled(false)
      setTwoFaMode(null)
      setTwoFaCode('')
      setTwoFaPassword('')
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || 'Failed to disable 2FA')
    } finally {
      setTwoFaBusy(false)
    }
  }

  const handleRegenerate = async () => {
    setTwoFaError('')
    setTwoFaBusy(true)
    try {
      const res = await api.post('/auth/2fa/regenerate-backup-codes', { current_password: twoFaPassword, code: twoFaCode.trim() })
      setBackupCodes(res.data?.backup_codes || [])
      setTwoFaMode(null)
      setTwoFaCode('')
      setTwoFaPassword('')
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || 'Failed to regenerate')
    } finally {
      setTwoFaBusy(false)
    }
  }

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true)
    try {
      const res = await api.get('/auth/sessions')
      setSessions(res.data?.sessions || [])
    } catch (err) {
      console.error('Failed to load sessions:', err)
    } finally {
      setSessionsLoading(false)
    }
  }, [])

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Revoke this session? It will be logged out.')) return
    const snapshot = sessions
    setSessions(prev => prev.filter(s => s.id !== sessionId))
    try {
      await api.delete(`/auth/sessions/${sessionId}`)
    } catch (err) {
      setSessions(snapshot)
      alert('Failed to revoke session')
    }
  }

  const handleLogoutAll = async () => {
    if (!window.confirm('Log out of all other devices? Your current session will stay active.')) return
    try {
      await api.post('/auth/logout-all')
      await fetchSessions()
    } catch (err) {
      alert('Failed to log out other sessions')
    }
  }

  useEffect(() => {
    if (!isPushSupported()) {
      setPushSupported(false)
      return
    }
    setPushSupported(true)
    getPushState().then(s => {
      setPushEnabled(s.enabled)
      setPushPermission(s.permission)
    }).catch(() => {})
  }, [])

  const handleToggleBrowserPush = async () => {
    if (!pushSupported) return
    setSaveError(null)
    const next = !(notifications.browserNotifications && pushEnabled)
    try {
      if (next) {
        await enablePush()
        setPushEnabled(true)
        setPushPermission('granted')
        setNotifications(n => ({ ...n, browserNotifications: true }))
      } else {
        await disablePush()
        setPushEnabled(false)
        setNotifications(n => ({ ...n, browserNotifications: false }))
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to toggle push notifications')
      const s = await getPushState()
      setPushEnabled(s.enabled)
      setPushPermission(s.permission)
    }
  }

  const [preferencesLoading, setPreferencesLoading] = useState(false)
  const [preferencesSaving, setPreferencesSaving] = useState(false)
  const [sendingLoading, setSendingLoading] = useState(false)
  const [sendingSaving, setSendingSaving] = useState(false)
  const [appearanceSaving, setAppearanceSaving] = useState(false)

  const fetchPreferences = useCallback(async () => {
    setPreferencesLoading(true)
    try {
      const res = await api.get('/api/v1/settings/preferences')
      const s = res.data?.settings || {}
      setPreferences(p => ({
        ...p,
        autoTagLeadStatus: s.auto_tag_lead_status ?? p.autoTagLeadStatus,
        oooDetection: s.ooo_detection ?? p.oooDetection,
        aiSuggestedReplies: s.ai_suggested_replies ?? p.aiSuggestedReplies,
        aiInboxManager: s.ai_inbox_manager ?? p.aiInboxManager,
        viewBounceInUnibox: s.view_bounce_in_unibox ?? p.viewBounceInUnibox,
        autoPauseHighBounce: s.auto_pause_high_bounce ?? p.autoPauseHighBounce,
        singleAccountPerGap: s.single_account_per_gap ?? p.singleAccountPerGap,
        aiSmartSending: s.ai_smart_sending ?? p.aiSmartSending,
        plainTextFirstEmail: s.plain_text_first_email ?? p.plainTextFirstEmail,
      }))
    } catch (err) {
      console.error('Failed to load preferences:', err)
    } finally {
      setPreferencesLoading(false)
    }
  }, [])

  const fetchEmailSending = useCallback(async () => {
    setSendingLoading(true)
    try {
      const res = await api.get('/api/v1/settings/email_sending')
      const s = res.data?.settings || {}
      setSendingSettings(prev => ({
        ...prev,
        campaignLimitResetTimezone: s.campaign_limit_reset_timezone ?? prev.campaignLimitResetTimezone,
        minTimeGap: s.min_time_gap ?? prev.minTimeGap,
        randomDelay: s.random_delay ?? prev.randomDelay,
        dailySendingLimit: s.daily_sending_limit ?? prev.dailySendingLimit,
        openTracking: s.open_tracking ?? prev.openTracking,
        linkTracking: s.link_tracking ?? prev.linkTracking,
        unsubscribeHeader: s.unsubscribe_header ?? prev.unsubscribeHeader,
        stopOnReply: s.stop_on_reply ?? prev.stopOnReply,
        campaignSlowRamp: s.campaign_slow_ramp ?? prev.campaignSlowRamp,
        espMatching: s.esp_matching ?? prev.espMatching,
      }))
    } catch (err) {
      console.error('Failed to load email sending settings:', err)
    } finally {
      setSendingLoading(false)
    }
  }, [])

  const fetchAppearance = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/settings/appearance')
      const s = res.data?.settings || {}
      if (s.theme) setTheme(s.theme)
      if (s.accent_color) setAccentColor(s.accent_color)
      if (s.density) setDensity(s.density)
    } catch (err) {
      console.error('Failed to load appearance settings:', err)
    }
  }, [setTheme, setAccentColor, setDensity])

  useEffect(() => {
    if (activeSection === 'preferences') { fetchPreferences(); fetchAiLabels() }
    if (activeSection === 'email-sending') fetchEmailSending()
    if (activeSection === 'appearance') fetchAppearance()
    if (activeSection === 'security') { fetchSessions(); fetchTwoFaStatus() }
  }, [activeSection, fetchPreferences, fetchEmailSending, fetchAppearance, fetchAiLabels, fetchSessions, fetchTwoFaStatus])

  // Fetch notification settings from backend
  const fetchNotifications = useCallback(async () => {
    setNotificationsLoading(true)
    try {
      const res = await api.get('/api/v1/settings/notifications')
      const s = res.data?.settings || {}
      setNotifications({
        campaignCompleted: s.campaign_completed ?? true,
        newReplies: s.new_replies ?? true,
        weeklyReport: s.weekly_report ?? true,
        bounceAlerts: s.bounce_alerts ?? true,
        positiveReply: s.positive_reply ?? true,
        dailyDigest: s.daily_digest ?? false,
        teamActivity: s.team_activity ?? false,
        systemUpdates: s.system_updates ?? true,
        notificationRecipient: s.notification_recipient ?? 'owner',
        audioEnabled: (() => { const v = s.audio_enabled ?? false; setAudioOnGlobally(v); return v })(),
        emailNotifications: s.email_notifications ?? true,
        browserNotifications: s.browser_notifications ?? true,
        securityAlerts: s.security_alerts ?? true,
        productUpdates: s.product_updates ?? false,
        monthlyReport: s.monthly_report ?? true,
      })
    } catch (err) {
      console.error('Failed to load notification settings:', err)
    } finally {
      setNotificationsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeSection === 'notifications') fetchNotifications()
  }, [activeSection, fetchNotifications])

  // ---------- Billing ----------
  const [billing, setBilling] = useState(null) // { plan, subscription, usage }
  const [billingLoading, setBillingLoading] = useState(false)
  const [billingError, setBillingError] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [planActionLoading, setPlanActionLoading] = useState(null) // plan id being processed
  const [creditPricing, setCreditPricing] = useState([])
  const [creditPricingLoading, setCreditPricingLoading] = useState(false)
  const [availablePlans, setAvailablePlans] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)

  const fetchBilling = useCallback(async () => {
    setBillingLoading(true)
    setBillingError(null)
    try {
      const res = await api.get('/api/v1/billing/plan')
      setBilling(res.data)
    } catch (err) {
      console.error('Failed to load billing:', err)
      setBillingError('Failed to load billing information.')
    } finally {
      setBillingLoading(false)
    }
  }, [])

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true)
    try {
      const res = await api.get('/api/v1/billing/invoices', { params: { limit: 10 } })
      setInvoices(res.data?.invoices || [])
    } catch (err) {
      console.error('Failed to load invoices:', err)
    } finally {
      setInvoicesLoading(false)
    }
  }, [])

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true)
    try {
      const res = await api.get('/api/v1/billing/plans')
      const raw = Array.isArray(res.data) ? res.data : (res.data?.plans || [])
      setAvailablePlans(
        raw
          .filter(p => p?.id && (p.active ?? true))
          .map(normalizeServerPlan)
      )
    } catch (err) {
      console.error('Failed to load plans:', err)
      setAvailablePlans([]) // falls back to BILLING_PLANS in the UI
    } finally {
      setPlansLoading(false)
    }
  }, [])

  const fetchCreditPricing = useCallback(async () => {
    setCreditPricingLoading(true)
    try {
      const res = await api.get('/api/v1/billing/credit-pricing')
      setCreditPricing(res.data?.items || [])
    } catch (err) {
      console.error('Failed to load credit pricing:', err)
    } finally {
      setCreditPricingLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeSection === 'billing') {
      fetchBilling()
      fetchInvoices()
      fetchPlans()
      fetchCreditPricing()
    }
  }, [activeSection, fetchBilling, fetchInvoices, fetchPlans, fetchCreditPricing])

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/api/v1/billing/invoices/${invoiceId}/download`)
      const url = res.data?.url
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
      else alert('Invoice PDF is not available yet.')
    } catch (err) {
      console.error('Invoice download failed:', err)
      alert('Failed to download invoice.')
    }
  }

  const handleSelectPlan = async (planId) => {
    if (!planId) return
    setPlanActionLoading(planId)
    try {
      const res = await api.post('/api/v1/billing/subscribe', { plan_id: planId })
      const data = res.data || {}

      // Free plan or immediate switch
      if (data.ok || data.provider === 'none' || planId === 'free') {
        await fetchBilling()
        await fetchInvoices()
        await fetchPlans()
        return
      }

      const subscriptionId = data.subscription_id || data.razorpay_subscription_id
      const razorpayKey = data.razorpay_key || data.razorpay_key_id
      const planLabel = data.plan_id || data.plan?.id || data.plan?.name || planId

      if (data.provider !== 'razorpay' || !subscriptionId || !razorpayKey) {
        alert('Payment provider is not configured yet. Please try again later.')
        return
      }

      const loaded = await loadRazorpay()
      if (!loaded) {
        alert('Failed to load Razorpay checkout. Check your connection and try again.')
        return
      }

      const rzp = new window.Razorpay({
        key: razorpayKey,
        subscription_id: subscriptionId,
        name: 'Synxreach',
        description: `${planLabel} plan`,
        handler: async (response) => {
          try {
            await api.post('/api/v1/billing/verify-payment', {
              plan_id: planId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            })
            await fetchBilling()
            await fetchInvoices()
          } catch (err) {
            console.error('Verify payment failed:', err)
            alert('Payment verification failed. Please contact support if you were charged.')
          } finally {
            setPlanActionLoading(null)
          }
        },
        theme: { color: '#6366f1' },
        modal: { ondismiss: () => setPlanActionLoading(null) },
      })
      rzp.open()
      return // handler / ondismiss clears loading
    } catch (err) {
      console.error('Subscribe failed:', err)
      if (err?.response?.status === 501) {
        alert('Payments are not enabled on this environment yet.')
      } else {
        alert('Failed to start checkout. Please try again.')
      }
    } finally {
      // For synchronous paths. Razorpay open() returns before handler fires, so
      // we only clear here if we didn't open a modal.
      if (!window.Razorpay || planId === 'free') setPlanActionLoading(null)
    }
  }

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription? Your plan stays active until the end of the current period.')) return
    try {
      await api.post('/api/v1/billing/cancel')
      await fetchBilling()
    } catch (err) {
      console.error('Cancel failed:', err)
      alert('Failed to cancel subscription.')
    }
  }
  // ---------- /Billing ----------



  const [teamMembers, setTeamMembers] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('Editor')

  const fetchTeamMembers = useCallback(async () => {
    setTeamLoading(true)
    setTeamError(null)
    try {
      const res = await api.get('/workspace/members')
      setTeamMembers(res.data || [])
    } catch (err) {
      setTeamError('Failed to load team members.')
      console.error('Team members error:', err)
    } finally {
      setTeamLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeSection === 'workspace') fetchTeamMembers()
  }, [activeSection, fetchTeamMembers])

  const [apiKeys, setApiKeys] = useState([])
  const [apiKeysLoading, setApiKeysLoading] = useState(false)
  const [showCreateKey, setShowCreateKey] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyScopes, setNewKeyScopes] = useState([])
  const [creatingKey, setCreatingKey] = useState(false)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null)
  const [copiedKeyId, setCopiedKeyId] = useState(null)

  const [webhooks, setWebhooks] = useState([])
  const [webhooksLoading, setWebhooksLoading] = useState(false)
  const [savingWebhookId, setSavingWebhookId] = useState(null)
  const [testingWebhookId, setTestingWebhookId] = useState(null)

  const handleSave = async () => {
    if (activeSection === 'account') {
      setSaveError(null)
      try {
        const payload = {
          name: `${profile.firstName} ${profile.lastName}`.trim(),
          email: profile.email,
          company: profile.company,
          timezone: profile.timezone,
          language: profile.language,
        }
        const res = await api.put('/users/me', payload)
        updateUser({ name: payload.name, email: payload.email, company: payload.company, timezone: payload.timezone, language: payload.language })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (err) {
        setSaveError(err.response?.data?.detail || 'Failed to save changes.')
      }
    } else if (activeSection === 'notifications') {
      setNotificationsSaving(true)
      setSaveError(null)
      try {
        await api.put('/api/v1/settings/notifications', {
          settings: {
            campaign_completed: notifications.campaignCompleted,
            new_replies: notifications.newReplies,
            weekly_report: notifications.weeklyReport,
            bounce_alerts: notifications.bounceAlerts,
            positive_reply: notifications.positiveReply,
            daily_digest: notifications.dailyDigest,
            team_activity: notifications.teamActivity,
            system_updates: notifications.systemUpdates,
            notification_recipient: notifications.notificationRecipient,
            audio_enabled: notifications.audioEnabled,
            email_notifications: notifications.emailNotifications,
            browser_notifications: notifications.browserNotifications,
            security_alerts: notifications.securityAlerts,
            product_updates: notifications.productUpdates,
            monthly_report: notifications.monthlyReport,
          }
        })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (err) {
        setSaveError(err.response?.data?.detail || 'Failed to save notification settings.')
      } finally {
        setNotificationsSaving(false)
      }
    } else if (activeSection === 'preferences') {
      setPreferencesSaving(true)
      setSaveError(null)
      try {
        await api.put('/api/v1/settings/preferences', {
          settings: {
            auto_tag_lead_status: preferences.autoTagLeadStatus,
            ooo_detection: preferences.oooDetection,
            ai_suggested_replies: preferences.aiSuggestedReplies,
            ai_inbox_manager: preferences.aiInboxManager,
            view_bounce_in_unibox: preferences.viewBounceInUnibox,
            auto_pause_high_bounce: preferences.autoPauseHighBounce,
            single_account_per_gap: preferences.singleAccountPerGap,
            ai_smart_sending: preferences.aiSmartSending,
            plain_text_first_email: preferences.plainTextFirstEmail,
          }
        })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (err) {
        setSaveError(err.response?.data?.detail || 'Failed to save preferences.')
      } finally {
        setPreferencesSaving(false)
      }
    } else if (activeSection === 'email-sending') {
      setSendingSaving(true)
      setSaveError(null)
      try {
        await api.put('/api/v1/settings/email_sending', {
          settings: {
            campaign_limit_reset_timezone: sendingSettings.campaignLimitResetTimezone,
            min_time_gap: sendingSettings.minTimeGap,
            random_delay: sendingSettings.randomDelay,
            daily_sending_limit: sendingSettings.dailySendingLimit,
            open_tracking: sendingSettings.openTracking,
            link_tracking: sendingSettings.linkTracking,
            unsubscribe_header: sendingSettings.unsubscribeHeader,
            stop_on_reply: sendingSettings.stopOnReply,
            campaign_slow_ramp: sendingSettings.campaignSlowRamp,
            esp_matching: sendingSettings.espMatching,
          }
        })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (err) {
        setSaveError(err.response?.data?.detail || 'Failed to save email sending settings.')
      } finally {
        setSendingSaving(false)
      }
    } else if (activeSection === 'appearance') {
      setAppearanceSaving(true)
      setSaveError(null)
      try {
        await api.put('/api/v1/settings/appearance', {
          settings: {
            theme,
            accent_color: accentColor,
            density,
          }
        })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (err) {
        setSaveError(err.response?.data?.detail || 'Failed to save appearance settings.')
      } finally {
        setAppearanceSaving(false)
      }
    } else {
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    }
  }

  const handlePasswordChange = async () => {
    if (!passwordForm.current || !passwordForm.newPw) return
    if (passwordForm.newPw !== passwordForm.confirm) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' })
      return
    }
    setPasswordSaving(true)
    setPasswordMsg(null)
    try {
      await api.put('/auth/change-password', { current_password: passwordForm.current, new_password: passwordForm.newPw })
      setPasswordMsg({ type: 'success', text: 'Password updated successfully.' })
      setPasswordForm({ current: '', newPw: '', confirm: '' })
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to update password.' })
    } finally {
      setPasswordSaving(false)
    }
  }

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return
    setInviteLoading(true)
    try {
      const res = await api.post('/workspace/invite', { email: inviteEmail.trim(), role: inviteRole })
      const newMember = res.data || { id: Date.now(), name: inviteEmail.split('@')[0], email: inviteEmail, role: inviteRole, status: 'pending' }
      setTeamMembers(prev => [...prev, newMember])
      setInviteEmail('')
    } catch (err) {
      console.error('Invite error:', err)
    } finally {
      setInviteLoading(false)
    }
  }

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/workspace/members/${memberId}`)
      setTeamMembers(prev => prev.filter(m => m.id !== memberId))
    } catch (err) {
      console.error('Remove member error:', err)
    }
  }

  const handleChangeMemberRole = async (memberId, newRole) => {
    try {
      await api.patch(`/workspace/members/${memberId}`, { role: newRole })
      setTeamMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m))
    } catch (err) {
      console.error('Change role error:', err)
    }
  }

  const handleAddLabel = async () => {
    const name = newLabelName.trim()
    if (!name) return
    const colors = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6']
    const color = colors[aiLabels.length % colors.length]
    try {
      const res = await api.post('/api/v1/settings/ai-labels', { name, description: '', enabled: true, color })
      setAiLabels(prev => [...prev, res.data])
      setNewLabelName('')
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add label'
      alert(msg)
    }
  }

  const handleToggleLabel = async (label) => {
    const next = !label.enabled
    setAiLabels(prev => prev.map(l => l.id === label.id ? { ...l, enabled: next } : l))
    try {
      await api.put(`/api/v1/settings/ai-labels/${label.id}`, { enabled: next })
    } catch (err) {
      setAiLabels(prev => prev.map(l => l.id === label.id ? { ...l, enabled: !next } : l))
    }
  }

  const handleReorderLabels = async (newOrder) => {
    const snapshot = aiLabels
    setAiLabels(newOrder)
    try {
      await api.patch('/api/v1/settings/ai-labels/reorder', { ids: newOrder.map(l => l.id) })
    } catch (err) {
      setAiLabels(snapshot)
      alert('Failed to reorder labels')
    }
  }

  const handleDragStart = (e, idx) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(idx))
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e, dropIdx) => {
    e.preventDefault()
    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10)
    if (Number.isNaN(fromIdx) || fromIdx === dropIdx) return
    const next = [...aiLabels]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(dropIdx, 0, moved)
    handleReorderLabels(next)
  }

  const handleDeleteLabel = async (label) => {
    if (!window.confirm(`Delete label "${label.name}"?`)) return
    const snapshot = aiLabels
    setAiLabels(prev => prev.filter(l => l.id !== label.id))
    try {
      await api.delete(`/api/v1/settings/ai-labels/${label.id}`)
    } catch (err) {
      setAiLabels(snapshot)
      alert('Failed to delete label')
    }
  }

  const toggleScope = (scope) => {
    setNewKeyScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope])
  }

  // ---- API Keys CRUD ----
  const fetchApiKeys = useCallback(async () => {
    setApiKeysLoading(true)
    try {
      const res = await api.get('/api/v1/settings/api-keys')
      setApiKeys(res.data?.items || res.data || [])
    } catch (err) {
      console.error('Failed to load API keys:', err)
    } finally {
      setApiKeysLoading(false)
    }
  }, [])

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return
    setCreatingKey(true)
    try {
      const res = await api.post('/api/v1/settings/api-keys', { name: newKeyName.trim(), environment: 'live', scopes: newKeyScopes })
      setNewlyCreatedKey(res.data.key || res.data.full_key || null)
      setApiKeys(prev => [...prev, res.data])
      setShowCreateKey(false)
      setNewKeyName('')
      setNewKeyScopes([])
    } catch (err) {
      console.error('Failed to create API key:', err)
    } finally {
      setCreatingKey(false)
    }
  }

  const handleRevokeApiKey = async (keyId) => {
    try {
      await api.delete(`/api/v1/settings/api-keys/${keyId}`)
      setApiKeys(prev => prev.filter(k => k.id !== keyId))
      if (newlyCreatedKey) setNewlyCreatedKey(null)
    } catch (err) {
      console.error('Failed to revoke API key:', err)
    }
  }

  const handleCopyKey = (keyValue, keyId) => {
    navigator.clipboard.writeText(keyValue).then(() => {
      setCopiedKeyId(keyId)
      setTimeout(() => setCopiedKeyId(null), 2000)
    })
  }

  // ---- Webhooks CRUD ----
  const fetchWebhooks = useCallback(async () => {
    setWebhooksLoading(true)
    try {
      const res = await api.get('/api/v1/settings/webhooks')
      setWebhooks(res.data?.items || res.data || [])
    } catch (err) {
      console.error('Failed to load webhooks:', err)
    } finally {
      setWebhooksLoading(false)
    }
  }, [])

  const handleCreateWebhook = async () => {
    try {
      const res = await api.post('/api/v1/settings/webhooks', { url: '', events: [], description: '' })
      setWebhooks(prev => [...prev, res.data])
    } catch (err) {
      // Fallback: add locally if backend not ready
      setWebhooks(prev => [...prev, { id: Date.now(), url: '', events: [], active: true }])
    }
  }

  const handleUpdateWebhook = async (webhookId, updates) => {
    setSavingWebhookId(webhookId)
    try {
      await api.put(`/api/v1/settings/webhooks/${webhookId}`, { url: updates.url, events: updates.events, is_active: updates.active })
    } catch (err) {
      console.error('Failed to update webhook:', err)
    } finally {
      setSavingWebhookId(null)
    }
  }

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/api/v1/settings/webhooks/${webhookId}`)
      setWebhooks(prev => prev.filter(w => w.id !== webhookId))
    } catch (err) {
      console.error('Failed to delete webhook:', err)
      setWebhooks(prev => prev.filter(w => w.id !== webhookId))
    }
  }

  const handleToggleWebhook = async (webhookId) => {
    const wh = webhooks.find(w => w.id === webhookId)
    if (!wh) return
    const updated = { ...wh, active: !wh.active }
    setWebhooks(prev => prev.map(w => w.id === webhookId ? updated : w))
    handleUpdateWebhook(webhookId, updated)
  }

  const handleWebhookUrlChange = (webhookId, url) => {
    setWebhooks(prev => prev.map(w => w.id === webhookId ? { ...w, url } : w))
  }

  const handleWebhookUrlBlur = (webhookId) => {
    const wh = webhooks.find(w => w.id === webhookId)
    if (wh && wh.url) handleUpdateWebhook(webhookId, wh)
  }

  const handleToggleWebhookEvent = (webhookId, event) => {
    const wh = webhooks.find(w => w.id === webhookId)
    if (!wh) return
    const events = wh.events.includes(event) ? wh.events.filter(e => e !== event) : [...wh.events, event]
    const updated = { ...wh, events }
    setWebhooks(prev => prev.map(w => w.id === webhookId ? updated : w))
    handleUpdateWebhook(webhookId, updated)
  }

  const handleTestWebhook = async (webhookId) => {
    setTestingWebhookId(webhookId)
    try {
      await api.post(`/api/v1/settings/webhooks/${webhookId}/test`)
    } catch (err) {
      console.error('Failed to test webhook:', err)
    } finally {
      setTestingWebhookId(null)
    }
  }

  // Fetch API keys & webhooks when integrations tab is active
  useEffect(() => {
    if (activeSection === 'integrations') {
      fetchApiKeys()
      fetchWebhooks()
    }
  }, [activeSection, fetchApiKeys, fetchWebhooks])

  const renderContent = () => {
    switch (activeSection) {
      case 'account':
        return (
          <>
            <div className="st-section-header">
              <h3>Account Settings</h3>
              <p>Manage your account information and preferences</p>
            </div>

            {saveError && (
              <div className="st-alert st-alert--error">
                <AlertTriangle size={16} /> {saveError}
              </div>
            )}

            <Card title="Profile Information" description="Your personal details and account info">
              {profileLoading ? (
                <div className="st-loading"><Loader2 size={20} className="spin" /> Loading profile...</div>
              ) : (
                <div className="st-form">
                  <div className="st-form-row">
                    <div className="st-field">
                      <label>First Name</label>
                      <input type="text" value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })} />
                    </div>
                    <div className="st-field">
                      <label>Last Name</label>
                      <input type="text" value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })} />
                    </div>
                  </div>
                  <div className="st-field">
                    <label>Email Address</label>
                    <input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} />
                  </div>
                  <div className="st-field">
                    <label>Company Name</label>
                    <input type="text" value={profile.company} onChange={e => setProfile({ ...profile, company: e.target.value })} />
                  </div>
                  <div className="st-form-row">
                    <div className="st-field">
                      <label>Timezone</label>
                      <select value={profile.timezone} onChange={e => setProfile({ ...profile, timezone: e.target.value })}>
                        {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                      </select>
                    </div>
                    <div className="st-field">
                      <label>Dashboard Language</label>
                      <select value={profile.language} onChange={e => setProfile({ ...profile, language: e.target.value })}>
                        {LANGUAGES.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="st-field">
                    <label>Profile Picture</label>
                    <div className="st-avatar-upload">
                      <div className="st-avatar-circle">{profile.firstName.charAt(0) || '?'}</div>
                      <div className="st-avatar-meta">
                        <button className="btn btn-ghost btn-sm" onClick={async () => {
                          const input = document.createElement('input')
                          input.type = 'file'
                          input.accept = 'image/*'
                          input.onchange = async (e) => {
                            const file = e.target.files[0]
                            if (!file || file.size > 2 * 1024 * 1024) return
                            const formData = new FormData()
                            formData.append('avatar', file)
                            try {
                              const res = await api.post('/users/me/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                              updateUser({ avatar: res.data.avatar_url })
                            } catch (err) {
                              console.error('Avatar upload error:', err)
                            }
                          }
                          input.click()
                        }}>Upload Image</button>
                        <span className="st-hint">JPG, PNG or GIF. Max 2MB.</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <Card title="Danger Zone" variant="danger">
              <SettingRow icon={LogOut} label="Log Out of All Devices" description="Signs out of all active sessions across all devices">
                <button className="btn btn-ghost btn-sm" onClick={async () => {
                  try { await api.post('/auth/logout-all') } catch (e) { /* ignore */ }
                }}>Log Out All</button>
              </SettingRow>
              <SettingRow icon={Trash2} label="Delete Account" description="Permanently delete your account and all data">
                <button className="btn btn-sm st-btn-danger" onClick={() => {
                  if (window.confirm('Are you sure? This action is irreversible.')) {
                    api.delete('/users/me').catch(console.error)
                  }
                }}>Delete Account</button>
              </SettingRow>
            </Card>
          </>
        )

      case 'preferences':
        return (
          <>
            <div className="st-section-header">
              <h3>Preferences</h3>
              <p>Configure AI, inbox management, and email sending behavior</p>
            </div>

            <Card
              title="Custom AI Labels"
              description="Add custom labels for AI to tag replies automatically"
              action={
                <div className="st-inline-form">
                  <input type="text" placeholder="New label name..." value={newLabelName}
                    onChange={e => setNewLabelName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddLabel()}
                    className="st-inline-input" />
                  <button className="btn btn-primary btn-sm" onClick={handleAddLabel}><Plus size={14} /> Add</button>
                </div>
              }
            >
              <div className="st-labels-list">
                {aiLabelsLoading ? (
                  <div className="st-loading"><Loader2 size={16} className="spin" /> Loading labels...</div>
                ) : aiLabels.length === 0 ? (
                  <div className="st-empty">No labels yet. Add one above.</div>
                ) : aiLabels.map((label, idx) => (
                  <div key={label.id} className="st-label-item"
                    draggable
                    onDragStart={e => handleDragStart(e, idx)}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, idx)}>
                    <span style={{ cursor: 'grab', opacity: 0.5, marginRight: 4, fontSize: 14 }} title="Drag to reorder">⋮⋮</span>
                    <div className="st-label-dot" style={{ background: label.color }} />
                    <div className="st-label-info">
                      <span className="st-label-name">{label.name}</span>
                      {label.description && <span className="st-label-desc">{label.description}</span>}
                    </div>
                    <Toggle checked={label.enabled} onChange={() => handleToggleLabel(label)} />
                    <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteLabel(label)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="st-card-footer">
                <button className="btn btn-ghost btn-sm"><TestTube size={14} /> Test AI Labels</button>
                <span className="st-hint">Verify replies are tagged correctly based on your label descriptions</span>
              </div>
            </Card>

            <Card title="AI & Lead Management" description="Configure how AI processes and categorizes your leads">
              <SettingRow icon={Tag} label="Automatically Tag Lead Status"
                description="AI automatically tags replies as Interested, Not Interested, or Out-of-Office">
                <Toggle checked={preferences.autoTagLeadStatus} onChange={() => setPreferences(p => ({ ...p, autoTagLeadStatus: !p.autoTagLeadStatus }))} />
              </SettingRow>
              <SettingRow icon={Clock} label="Out-of-Office Detection"
                description="Automatically detect and tag out-of-office auto-replies">
                <Toggle checked={preferences.oooDetection} onChange={() => setPreferences(p => ({ ...p, oooDetection: !p.oooDetection }))} />
              </SettingRow>
              <SettingRow icon={Brain} label="AI Suggested Replies"
                description="AI analyzes context and provides relevant reply drafts" badge={{ text: 'AI', type: 'ai' }}>
                <Toggle checked={preferences.aiSuggestedReplies} onChange={() => setPreferences(p => ({ ...p, aiSuggestedReplies: !p.aiSuggestedReplies }))} />
              </SettingRow>
            </Card>

            <Card title="Inbox Management" description="Configure inbox behavior and message handling">
              <SettingRow icon={Inbox} label="AI Inbox Manager"
                description="Automatically schedule email replies when a lead's first response is detected" badge={{ text: 'AI', type: 'ai' }}>
                <Toggle checked={preferences.aiInboxManager} onChange={() => setPreferences(p => ({ ...p, aiInboxManager: !p.aiInboxManager }))} />
              </SettingRow>
              <SettingRow icon={Mail} label="View Bounce Messages in Inbox"
                description="See bounce messages directly in inbox without logging into the original mailbox">
                <Toggle checked={preferences.viewBounceInUnibox} onChange={() => setPreferences(p => ({ ...p, viewBounceInUnibox: !p.viewBounceInUnibox }))} />
              </SettingRow>
            </Card>

            <Card title="Email Sending Behavior" description="Control how emails are sent and optimized">
              <SettingRow icon={AlertTriangle} label="Auto-Pause on High Bounce Rate"
                description="Automatically pause campaigns with high bounce rates to protect sender reputation">
                <Toggle checked={preferences.autoPauseHighBounce} onChange={() => setPreferences(p => ({ ...p, autoPauseHighBounce: !p.autoPauseHighBounce }))} />
              </SettingRow>
              <SettingRow icon={RefreshCw} label="Single Sending Account Per Time Gap"
                description="Use only one sending account per time gap for more natural sending patterns">
                <Toggle checked={preferences.singleAccountPerGap} onChange={() => setPreferences(p => ({ ...p, singleAccountPerGap: !p.singleAccountPerGap }))} />
              </SettingRow>
              <SettingRow icon={Zap} label="AI Smart Sending"
                description="AI-powered sending optimization to prevent spam triggers" badge={{ text: 'AI', type: 'ai' }}>
                <Toggle checked={preferences.aiSmartSending} onChange={() => setPreferences(p => ({ ...p, aiSmartSending: !p.aiSmartSending }))} />
              </SettingRow>
              <SettingRow icon={Send} label="Plain Text First Email"
                description="Send the first campaign email as plain text for improved deliverability">
                <Toggle checked={preferences.plainTextFirstEmail} onChange={() => setPreferences(p => ({ ...p, plainTextFirstEmail: !p.plainTextFirstEmail }))} />
              </SettingRow>
            </Card>
          </>
        )

      case 'email-sending':
        return (
          <>
            <div className="st-section-header">
              <h3>Email Sending</h3>
              <p>Configure global email sending limits, tracking, and delivery settings</p>
            </div>

            <Card title="Sending Limits" description="Set global limits for email sending">
              <div className="st-form">
                <div className="st-form-row">
                  <div className="st-field">
                    <label>Daily Sending Limit (per account)</label>
                    <input type="number" value={sendingSettings.dailySendingLimit}
                      onChange={e => setSendingSettings(s => ({ ...s, dailySendingLimit: parseInt(e.target.value) || 0 }))} />
                    <span className="st-hint">Recommended: 30 campaign + 10 warmup emails</span>
                  </div>
                  <div className="st-field">
                    <label>Campaign Limit Reset Timezone</label>
                    <select value={sendingSettings.campaignLimitResetTimezone}
                      onChange={e => setSendingSettings(s => ({ ...s, campaignLimitResetTimezone: e.target.value }))}>
                      {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                    </select>
                    <span className="st-hint">When daily campaign email limits reset</span>
                  </div>
                </div>
                <div className="st-form-row">
                  <div className="st-field">
                    <label>Minimum Time Gap (minutes)</label>
                    <input type="number" value={sendingSettings.minTimeGap}
                      onChange={e => setSendingSettings(s => ({ ...s, minTimeGap: parseInt(e.target.value) || 0 }))} />
                    <span className="st-hint">Minimum wait between sending each email</span>
                  </div>
                  <div className="st-field">
                    <label>Random Delay (minutes)</label>
                    <input type="number" value={sendingSettings.randomDelay}
                      onChange={e => setSendingSettings(s => ({ ...s, randomDelay: parseInt(e.target.value) || 0 }))} />
                    <span className="st-hint">Additional random delay on top of base gap</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Tracking & Compliance" description="Configure email tracking and compliance settings">
              <SettingRow icon={Eye} label="Open Tracking" description="Track whether leads opened your emails">
                <Toggle checked={sendingSettings.openTracking} onChange={() => setSendingSettings(s => ({ ...s, openTracking: !s.openTracking }))} />
              </SettingRow>
              <SettingRow icon={Link2} label="Link Tracking" description="Track whether leads clicked links in your emails">
                <Toggle checked={sendingSettings.linkTracking} onChange={() => setSendingSettings(s => ({ ...s, linkTracking: !s.linkTracking }))} />
              </SettingRow>
              <SettingRow icon={Mail} label="Unsubscribe Link Header" description="Insert one-click opt-out header for compliance (recommended)">
                <Toggle checked={sendingSettings.unsubscribeHeader} onChange={() => setSendingSettings(s => ({ ...s, unsubscribeHeader: !s.unsubscribeHeader }))} />
              </SettingRow>
              <SettingRow icon={MessageSquare} label="Stop Sending on Reply" description="Automatically pause follow-up emails when a lead replies">
                <Toggle checked={sendingSettings.stopOnReply} onChange={() => setSendingSettings(s => ({ ...s, stopOnReply: !s.stopOnReply }))} />
              </SettingRow>
            </Card>

            <Card title="Advanced Sending" description="Fine-tune sending behavior for better results">
              <SettingRow icon={BarChart3} label="Campaign Slow Ramp"
                description="Gradually increase sending by 2 emails/day until max is reached">
                <Toggle checked={sendingSettings.campaignSlowRamp} onChange={() => setSendingSettings(s => ({ ...s, campaignSlowRamp: !s.campaignSlowRamp }))} />
              </SettingRow>
              <SettingRow icon={ShieldCheck} label="ESP Matching"
                description="Match sending ESP to recipients' ESPs for better deliverability" badge={{ text: 'Beta', type: 'beta' }}>
                <Toggle checked={sendingSettings.espMatching} onChange={() => setSendingSettings(s => ({ ...s, espMatching: !s.espMatching }))} />
              </SettingRow>
            </Card>

            <Card title="Warmup Defaults" description="Default settings for email warmup on new accounts">
              <div className="st-info-banner">
                <Info size={16} />
                <span>Per-account warmup settings are managed in <strong>Email Accounts</strong>. These are defaults for newly connected accounts.</span>
              </div>
              <div className="st-form" style={{ marginTop: '1rem' }}>
                <div className="st-form-row">
                  <div className="st-field">
                    <label>Default Daily Warmup Limit</label>
                    <input type="number" defaultValue={10} />
                    <span className="st-hint">Max warmup emails per day for new accounts</span>
                  </div>
                  <div className="st-field">
                    <label>Default Reply Rate (%)</label>
                    <input type="number" defaultValue={30} />
                    <span className="st-hint">Recommended: 30% for optimal warmup</span>
                  </div>
                </div>
              </div>
            </Card>
          </>
        )

      case 'notifications':
        return (
          <>
            <div className="st-section-header">
              <h3>Notifications</h3>
              <p>Choose how and when you want to be notified</p>
            </div>

            {notificationsLoading ? (
              <div className="st-loading-state">
                <Loader2 size={24} className="spin" />
                <span>Loading notification settings...</span>
              </div>
            ) : (
              <>
                {saveError && activeSection === 'notifications' && (
                  <div className="st-alert st-alert--error" style={{ marginBottom: '1rem' }}>
                    <AlertTriangle size={16} />
                    {' '}{saveError}
                  </div>
                )}

                <Card title="Notification Channels" description="Choose how you want to receive notifications">
                  <SettingRow icon={Mail} label="Email Notifications" description="Receive notifications via email">
                    <Toggle checked={notifications.emailNotifications} onChange={() => setNotifications(n => ({ ...n, emailNotifications: !n.emailNotifications }))} />
                  </SettingRow>
                  <SettingRow icon={Monitor} label="Browser Notifications" description={pushSupported ? (pushPermission === 'denied' ? 'Permission blocked — enable in browser settings' : 'Show desktop push notifications in your browser') : 'Not supported in this browser'}>
                    <Toggle checked={notifications.browserNotifications && pushEnabled}
                      disabled={!pushSupported || pushPermission === 'denied'}
                      onChange={() => handleToggleBrowserPush()} />
                  </SettingRow>
                  <SettingRow icon={Volume2} label="Audio Notifications" description="Play a sound when new notifications arrive">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn btn-ghost btn-sm" type="button"
                        onClick={() => { setAudioOnGlobally(true); playChime() }}
                        title="Test sound">Test</button>
                      <Toggle checked={notifications.audioEnabled}
                        onChange={() => {
                          const next = !notifications.audioEnabled
                          setNotifications(n => ({ ...n, audioEnabled: next }))
                          setAudioOnGlobally(next)
                          if (next) playChime()
                        }} />
                    </div>
                  </SettingRow>
                </Card>

                <Card title="Campaign & Activity Alerts" description="Get notified about important campaign events">
                  <SettingRow icon={Megaphone} label="Campaign Completed" description="Get notified when a campaign finishes sending">
                    <Toggle checked={notifications.campaignCompleted} onChange={() => setNotifications(n => ({ ...n, campaignCompleted: !n.campaignCompleted }))} />
                  </SettingRow>
                  <SettingRow icon={MessageSquare} label="New Replies" description="Get notified when you receive a reply from a lead">
                    <Toggle checked={notifications.newReplies} onChange={() => setNotifications(n => ({ ...n, newReplies: !n.newReplies }))} />
                  </SettingRow>
                  <SettingRow icon={Check} label="Positive Reply Notification" description="Real-time alerts when leads respond positively">
                    <Toggle checked={notifications.positiveReply} onChange={() => setNotifications(n => ({ ...n, positiveReply: !n.positiveReply }))} />
                  </SettingRow>
                  <SettingRow icon={AlertTriangle} label="Bounce Alerts" description="Get alerted when bounce rate exceeds threshold">
                    <Toggle checked={notifications.bounceAlerts} onChange={() => setNotifications(n => ({ ...n, bounceAlerts: !n.bounceAlerts }))} />
                  </SettingRow>
                  <SettingRow icon={Users} label="Team Activity" description="Get notified about team member actions">
                    <Toggle checked={notifications.teamActivity} onChange={() => setNotifications(n => ({ ...n, teamActivity: !n.teamActivity }))} />
                  </SettingRow>
                </Card>

                <Card title="Reports & Digests" description="Scheduled summary notifications">
                  <SettingRow icon={Mail} label="Daily Digest" description="Receive a daily summary of all activity">
                    <Toggle checked={notifications.dailyDigest} onChange={() => setNotifications(n => ({ ...n, dailyDigest: !n.dailyDigest }))} />
                  </SettingRow>
                  <SettingRow icon={BarChart3} label="Weekly Report" description="Receive a weekly summary of campaign performance">
                    <Toggle checked={notifications.weeklyReport} onChange={() => setNotifications(n => ({ ...n, weeklyReport: !n.weeklyReport }))} />
                  </SettingRow>
                  <SettingRow icon={BarChart3} label="Monthly Report" description="Receive a monthly overview of account performance">
                    <Toggle checked={notifications.monthlyReport} onChange={() => setNotifications(n => ({ ...n, monthlyReport: !n.monthlyReport }))} />
                  </SettingRow>
                </Card>

                <Card title="System & Security" description="Important system and security notifications">
                  <SettingRow icon={SettingsIcon} label="System Updates" description="Notifications about system updates and maintenance">
                    <Toggle checked={notifications.systemUpdates} onChange={() => setNotifications(n => ({ ...n, systemUpdates: !n.systemUpdates }))} />
                  </SettingRow>
                  <SettingRow icon={ShieldCheck} label="Security Alerts" description="Login attempts, password changes, and suspicious activity"
                    badge={{ text: 'Required', type: 'required' }}>
                    <Toggle checked={notifications.securityAlerts} disabled={true} onChange={() => {}} />
                  </SettingRow>
                  <SettingRow icon={Zap} label="Product Updates" description="New features, improvements, and product announcements">
                    <Toggle checked={notifications.productUpdates} onChange={() => setNotifications(n => ({ ...n, productUpdates: !n.productUpdates }))} />
                  </SettingRow>
                </Card>

                <Card title="Delivery Preferences" description="Control who receives notifications">
                  <SettingRow icon={Users} label="Notification Recipients" description="Who should receive notifications for this workspace">
                    <select className="st-inline-select" value={notifications.notificationRecipient}
                      onChange={e => setNotifications(n => ({ ...n, notificationRecipient: e.target.value }))}>
                      <option value="owner">Owner Only</option>
                      <option value="all_admins">Owner & Admins</option>
                    </select>
                  </SettingRow>
                </Card>
              </>
            )}
          </>
        )

      case 'security':
        return (
          <>
            <div className="st-section-header">
              <h3>Security</h3>
              <p>Manage your security settings and active sessions</p>
            </div>

            <Card title="Change Password" description="Update your login credentials">
              <div className="st-form">
                {passwordMsg && (
                  <div className={`st-alert st-alert--${passwordMsg.type}`}>
                    {passwordMsg.type === 'error' ? <AlertTriangle size={16} /> : <Check size={16} />}
                    {' '}{passwordMsg.text}
                  </div>
                )}
                <div className="st-field">
                  <label>Current Password</label>
                  <input type="password" placeholder="Enter current password"
                    value={passwordForm.current}
                    onChange={e => setPasswordForm(f => ({ ...f, current: e.target.value }))} />
                </div>
                <div className="st-form-row">
                  <div className="st-field">
                    <label>New Password</label>
                    <input type="password" placeholder="Enter new password"
                      value={passwordForm.newPw}
                      onChange={e => setPasswordForm(f => ({ ...f, newPw: e.target.value }))} />
                  </div>
                  <div className="st-field">
                    <label>Confirm New Password</label>
                    <input type="password" placeholder="Confirm new password"
                      value={passwordForm.confirm}
                      onChange={e => setPasswordForm(f => ({ ...f, confirm: e.target.value }))} />
                  </div>
                </div>
                <div className="st-pw-rules">
                  <span>Password requirements:</span>
                  <ul>
                    <li>At least 8 characters long</li>
                    <li>Include uppercase and lowercase letters</li>
                    <li>Include at least one number</li>
                    <li>Include at least one special character</li>
                  </ul>
                </div>
                <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }}
                  onClick={handlePasswordChange} disabled={passwordSaving}>
                  {passwordSaving ? <><Loader2 size={16} className="spin" /> Updating...</> : 'Update Password'}
                </button>
              </div>
            </Card>

            <Card title="Two-Factor Authentication" description="Add an extra layer of security to your account">
              {twoFaError && <div className="st-alert st-alert--error"><AlertTriangle size={16} /> {twoFaError}</div>}

              <SettingRow icon={Shield}
                label={twoFaEnabled ? '2FA is Enabled' : '2FA is Disabled'}
                description={twoFaEnabled ? 'Your account is protected with TOTP-based 2FA' : 'Scan a QR code with Google Authenticator, 1Password, or Authy'}>
                {twoFaEnabled ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setTwoFaMode('regenerate'); setTwoFaError('') }}>Regenerate Codes</button>
                    <button className="btn btn-sm" style={{ background: '#ef4444', color: '#fff' }} onClick={() => { setTwoFaMode('disable'); setTwoFaError('') }}>Disable 2FA</button>
                  </div>
                ) : (
                  <button className="btn btn-primary btn-sm" disabled={twoFaBusy} onClick={handleStartSetup}>
                    {twoFaBusy ? <Loader2 size={14} className="spin" /> : 'Enable 2FA'}
                  </button>
                )}
              </SettingRow>

              {twoFaMode === 'setup' && twoFaSetup && (
                <div className="st-form" style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <img src={twoFaSetup.qr_data_uri} alt="2FA QR" style={{ width: 180, height: 180, background: '#fff', padding: 8, borderRadius: 8 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ marginBottom: 8 }}>1. Scan the QR with your authenticator app.</p>
                      <p style={{ marginBottom: 8 }}>Or enter this secret manually:</p>
                      <code style={{ wordBreak: 'break-all', display: 'block', padding: 8, background: 'var(--color-surface-2,#1a1a2e)', borderRadius: 4 }}>{twoFaSetup.secret}</code>
                    </div>
                  </div>
                  <div className="st-field" style={{ marginTop: '1rem' }}>
                    <label>2. Enter the 6-digit code from your app</label>
                    <input type="text" inputMode="numeric" maxLength={6} placeholder="123456"
                      value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)} />
                  </div>
                  <div className="st-form-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setTwoFaSetup(null); setTwoFaMode(null); setTwoFaCode('') }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" disabled={twoFaBusy || !twoFaCode.trim()} onClick={handleVerifySetup}>
                      {twoFaBusy ? <><Loader2 size={14} className="spin" /> Verifying...</> : 'Verify & Enable'}
                    </button>
                  </div>
                </div>
              )}

              {(twoFaMode === 'disable' || twoFaMode === 'regenerate') && (
                <div className="st-form" style={{ marginTop: '1rem' }}>
                  <p>{twoFaMode === 'disable' ? 'Confirm to turn off 2FA.' : 'Generate a new set of backup codes (old ones are invalidated).'}</p>
                  <div className="st-field"><label>Current Password</label>
                    <input type="password" value={twoFaPassword} onChange={e => setTwoFaPassword(e.target.value)} />
                  </div>
                  <div className="st-field"><label>2FA Code (TOTP or Backup)</label>
                    <input type="text" value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)} />
                  </div>
                  <div className="st-form-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setTwoFaMode(null); setTwoFaCode(''); setTwoFaPassword('') }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" disabled={twoFaBusy || !twoFaPassword || !twoFaCode.trim()}
                      onClick={twoFaMode === 'disable' ? handleDisable : handleRegenerate}>
                      {twoFaBusy ? <Loader2 size={14} className="spin" /> : (twoFaMode === 'disable' ? 'Disable' : 'Regenerate')}
                    </button>
                  </div>
                </div>
              )}

              {backupCodes && (
                <div className="st-alert st-alert--success" style={{ marginTop: '1rem', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <strong style={{ marginBottom: 8 }}>Save these backup codes — they'll only be shown once</strong>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, width: '100%' }}>
                    {backupCodes.map(c => <code key={c} style={{ padding: 8, background: 'var(--color-surface-2,#1a1a2e)', borderRadius: 4 }}>{c}</code>)}
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                    onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); }}>Copy all</button>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 8, alignSelf: 'flex-end' }} onClick={() => setBackupCodes(null)}>I've saved them</button>
                </div>
              )}
            </Card>

            <Card title="Active Sessions" description="Manage your active sessions across devices">
              {sessionsLoading ? (
                <div className="st-loading"><Loader2 size={20} className="spin" /> Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <div className="st-empty">No active sessions found.</div>
              ) : (
                <div className="st-sessions">
                  {sessions.map((session) => (
                    <div key={session.id} className={`st-session ${session.is_current ? 'st-session--current' : ''}`}>
                      <div className="st-session-icon"><Monitor size={18} /></div>
                      <div className="st-session-info">
                        <span className="st-session-device">
                          {session.user_agent || 'Unknown device'}
                          {session.is_current && <span className="st-badge st-badge--current">Current</span>}
                        </span>
                        <span className="st-session-meta">
                          {session.ip_address || '—'} &middot; last active {session.last_active_at ? new Date(session.last_active_at).toLocaleString() : '—'}
                        </span>
                      </div>
                      {!session.is_current && (
                        <button className="btn btn-ghost btn-sm" onClick={() => handleRevokeSession(session.id)}>Revoke</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="st-card-footer">
                <button className="btn btn-ghost btn-sm" onClick={handleLogoutAll}><LogOut size={14} /> Log Out of All Other Devices</button>
              </div>
            </Card>
          </>
        )

      case 'workspace':
        const isOwner = user?.workspace_role === 'Owner'
        return (
          <>
            <div className="st-section-header">
              <h3>Workspace & Team</h3>
              <p>Manage your workspace and team members</p>
            </div>

            <Card title="Team Members" description="Invite and manage team members"
              action={<span className="st-hint">{teamLoading ? '...' : `${teamMembers.length} members`}</span>}>
              {isOwner && (
                <div className="st-invite-bar">
                  <input type="email" placeholder="Enter email address..." value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleInviteMember()}
                    className="st-invite-input" />
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="st-inline-select">
                    <option value="Admin">Admin</option>
                    <option value="Editor">Editor</option>
                    <option value="View/VA">View/VA</option>
                  </select>
                  <button className="btn btn-primary btn-sm" onClick={handleInviteMember} disabled={inviteLoading}>
                    {inviteLoading ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Invite
                  </button>
                </div>
              )}

              {teamLoading ? (
                <div className="st-loading"><Loader2 size={20} className="spin" /> Loading team...</div>
              ) : teamError ? (
                <div className="st-alert st-alert--error"><AlertTriangle size={16} /> {teamError}
                  <button className="btn btn-ghost btn-sm" onClick={fetchTeamMembers} style={{ marginLeft: 8 }}>Retry</button>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="st-empty">No team members yet. Invite someone to get started.</div>
              ) : (
                <div className="st-team-list">
                  {teamMembers.map(member => (
                    <div key={member.id} className="st-team-row">
                      <div className="st-team-avatar">{(member.name || member.email || '?').charAt(0).toUpperCase()}</div>
                      <div className="st-team-info">
                        <span className="st-team-name">{member.name || member.email}</span>
                        <span className="st-team-email">{member.email}</span>
                      </div>
                      {member.role !== 'Owner' && isOwner ? (
                        <select
                          className="st-inline-select"
                          value={member.role}
                          onChange={e => handleChangeMemberRole(member.id, e.target.value)}>
                          <option value="Admin">Admin</option>
                          <option value="Editor">Editor</option>
                          <option value="View/VA">View/VA</option>
                        </select>
                      ) : (
                        <span className={`st-role-pill st-role-pill--${member.role.toLowerCase().replace('/', '-')}`}>{member.role}</span>
                      )}
                      <span className={`st-status-dot st-status-dot--${member.status}`}>{member.status}</span>
                      {member.role !== 'Owner' && isOwner && (
                        <button className="icon-btn danger" onClick={() => handleRemoveMember(member.id)}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Roles & Permissions" description="Understanding workspace roles">
              <div className="st-roles-grid">
                {[
                  { role: 'Owner', color: '#f59e0b', perms: ['Full access to all features', 'Manage billing & subscription', 'Manage team members', 'Delete workspace'] },
                  { role: 'Admin', color: '#ef4444', perms: ['Manage team & settings', 'Create & manage campaigns', 'View all analytics', 'Cannot delete workspace'] },
                  { role: 'Editor', color: '#3b82f6', perms: ['Create & manage campaigns', 'Manage contacts & templates', 'View analytics', 'Cannot manage team or billing'] },
                  { role: 'View/VA', color: '#22c55e', perms: ['Reply to messages', 'Update email settings', 'View campaigns & contacts', 'Cannot create or modify campaigns'] },
                ].map(r => (
                  <div key={r.role} className="st-role-card">
                    <div className="st-role-header">
                      <span className="st-role-dot" style={{ background: r.color }} />
                      <span className="st-role-name">{r.role}</span>
                    </div>
                    <ul className="st-role-perms">
                      {r.perms.map((p, i) => <li key={i}><Check size={12} /> {p}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )

      case 'integrations':
        return (
          <>
            <div className="st-section-header">
              <h3>Integrations</h3>
              <p>Manage API keys, webhooks, and third-party integrations</p>
            </div>

            <Card title="API Keys" description="Create and manage API keys for programmatic access"
              action={<button className="btn btn-primary btn-sm" onClick={() => setShowCreateKey(!showCreateKey)}><Plus size={14} /> Create API Key</button>}>

              {newlyCreatedKey && (
                <div className="st-alert st-alert--success" style={{ margin: '0 0 1rem' }}>
                  <ShieldCheck size={16} />
                  <div style={{ flex: 1 }}>
                    <strong>API key created!</strong> Copy it now — it won't be shown again.
                    <div className="st-api-key-row" style={{ marginTop: '0.5rem' }}>
                      <code style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>{newlyCreatedKey}</code>
                      <button className="icon-btn" title="Copy" onClick={() => handleCopyKey(newlyCreatedKey, 'new')}>
                        {copiedKeyId === 'new' ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                  <button className="icon-btn" onClick={() => setNewlyCreatedKey(null)}><Trash2 size={14} /></button>
                </div>
              )}

              {showCreateKey && (
                <div className="st-create-form">
                  <div className="st-form">
                    <div className="st-field">
                      <label>API Key Name</label>
                      <input type="text" placeholder="e.g., Production Key" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} />
                    </div>
                    <div className="st-field">
                      <label>Scopes</label>
                      <div className="st-chips">
                        {['campaigns', 'contacts', 'analytics', 'templates', 'accounts', 'inbox', 'webhooks'].map(scope => (
                          <button key={scope} className={`st-chip ${newKeyScopes.includes(scope) ? 'st-chip--active' : ''}`}
                            onClick={() => toggleScope(scope)}>
                            {newKeyScopes.includes(scope) && <Check size={12} />} {scope}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="st-form-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => { setShowCreateKey(false); setNewKeyName(''); setNewKeyScopes([]) }}>Cancel</button>
                      <button className="btn btn-primary btn-sm" disabled={creatingKey} onClick={handleCreateApiKey}>
                        {creatingKey ? <><Loader2 size={14} className="spin" /> Generating...</> : 'Generate Key'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {apiKeysLoading ? (
                <div className="st-loading"><Loader2 size={20} className="spin" /> Loading API keys...</div>
              ) : apiKeys.length === 0 ? (
                <div className="st-empty">No API keys yet. Create one to get started with the API.</div>
              ) : (
                <div className="st-api-list">
                  {apiKeys.map(key => (
                    <div key={key.id} className="st-api-row">
                      <div className="st-api-info">
                        <div className="st-api-top">
                          <span className="st-api-name">{key.name}</span>
                          <div className="st-api-scopes">{(key.scopes || []).map(s => <span key={s} className="st-scope-tag">{s}</span>)}</div>
                        </div>
                        <div className="st-api-key-row">
                          <code>{key.key || key.masked_key}</code>
                          <button className="icon-btn" title="Copy" onClick={() => handleCopyKey(key.key || key.masked_key, key.id)}>
                            {copiedKeyId === key.id ? <Check size={14} /> : <Copy size={14} />}
                          </button>
                        </div>
                        <span className="st-hint">Created: {key.created || key.created_at} &middot; Last used: {key.lastUsed || key.last_used_at || 'Never'}</span>
                      </div>
                      <button className="btn btn-ghost btn-sm st-btn-danger-text" onClick={() => handleRevokeApiKey(key.id)}>
                        <Trash2 size={14} /> Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="API Access" description="Use API keys to access Synxreach programmatically">
              <div className="st-info-banner">
                <Info size={16} />
                <div>
                  <p>Use your API keys to integrate Synxreach into your applications. All API requests should include the header:</p>
                  <div className="st-api-key-row" style={{ marginTop: '0.5rem' }}>
                    <code>X-API-Key: sx_live_your_api_key_here</code>
                  </div>
                  <p style={{ marginTop: '0.5rem' }}>Base URL: <code>{window.location.origin.includes('localhost') ? 'http://localhost:8000' : 'https://api.synxreach.com'}/api/v1/</code></p>
                  <p style={{ marginTop: '0.375rem', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    Rate limit: 100 requests/minute per key. Each key is scoped to the permissions you assign.
                  </p>
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <div className="st-row">
                  <div className="st-row-left">
                    <div className="st-row-icon"><BarChart3 size={18} /></div>
                    <div className="st-row-info">
                      <span className="st-row-label">Available API Endpoints</span>
                      <span className="st-row-desc">campaigns, contacts, analytics, templates, inbox, webhooks</span>
                    </div>
                  </div>
                </div>
                <div className="st-row">
                  <div className="st-row-left">
                    <div className="st-row-icon"><ShieldCheck size={18} /></div>
                    <div className="st-row-info">
                      <span className="st-row-label">Authentication</span>
                      <span className="st-row-desc">API keys use HMAC-SHA256 signing. Keys are scoped and rate-limited per workspace.</span>
                    </div>
                  </div>
                </div>
                <div className="st-row">
                  <div className="st-row-left">
                    <div className="st-row-icon"><Globe size={18} /></div>
                    <div className="st-row-info">
                      <span className="st-row-label">API Documentation</span>
                      <span className="st-row-desc">Full REST API reference with examples and SDKs</span>
                    </div>
                  </div>
                  <div className="st-row-right">
                    <button className="btn btn-ghost btn-sm" onClick={() => window.open('/api/docs', '_blank')}>View Docs</button>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Webhooks" description="Send real-time event data to external services"
              action={<button className="btn btn-primary btn-sm" onClick={handleCreateWebhook}>
                <Plus size={14} /> Add Webhook
              </button>}>
              {webhooksLoading ? (
                <div className="st-loading"><Loader2 size={20} className="spin" /> Loading webhooks...</div>
              ) : webhooks.length === 0 ? (
                <div className="st-empty">No webhooks configured. Add one to receive real-time event notifications.</div>
              ) : (
                <div className="st-webhooks">
                  {webhooks.map(wh => (
                    <div key={wh.id} className="st-webhook">
                      <div className="st-webhook-top">
                        <Webhook size={16} className="st-webhook-icon" />
                        <input type="url" className="st-webhook-input" placeholder="https://your-server.com/webhook"
                          value={wh.url}
                          onChange={e => handleWebhookUrlChange(wh.id, e.target.value)}
                          onBlur={() => handleWebhookUrlBlur(wh.id)} />
                        {savingWebhookId === wh.id && <Loader2 size={14} className="spin" />}
                        <button className="btn btn-ghost btn-sm" disabled={!wh.url || testingWebhookId === wh.id}
                          onClick={() => handleTestWebhook(wh.id)}>
                          {testingWebhookId === wh.id ? <Loader2 size={14} className="spin" /> : <Zap size={14} />} Test
                        </button>
                        <Toggle checked={wh.active} onChange={() => handleToggleWebhook(wh.id)} />
                        <button className="icon-btn danger" onClick={() => handleDeleteWebhook(wh.id)}><Trash2 size={16} /></button>
                      </div>
                      <div className="st-webhook-events">
                        <span className="st-hint">Events:</span>
                        {['send', 'reply', 'bounce', 'open', 'click', 'status_change', 'unsubscribe'].map(ev => (
                          <button key={ev} className={`st-chip st-chip--sm ${wh.events.includes(ev) ? 'st-chip--active' : ''}`}
                            onClick={() => handleToggleWebhookEvent(wh.id, ev)}>{ev.replace('_', ' ')}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}{/* end webhooks list */}
              <div className="st-info-banner" style={{ marginTop: '1rem' }}>
                <Info size={16} />
                <div>
                  <p>Webhook payloads are signed with HMAC-SHA256. Verify the <code>X-Synxreach-Signature</code> header to ensure authenticity. Failed deliveries are retried up to 5 times with exponential backoff.</p>
                </div>
              </div>
            </Card>

          </>
        )

      case 'billing': {
        const PLANS = availablePlans.length ? availablePlans : BILLING_PLANS
        const fmt = (n) => (n == null ? '∞' : Number(n).toLocaleString())
        const pct = (u, l) => (l == null || l === 0) ? 0 : Math.min(100, (u / l) * 100)

        if (billingLoading && !billing) {
          return (
            <>
              <div className="st-section-header">
                <h3>Billing & Usage</h3>
                <p>Track your plan, usage, and billing details</p>
              </div>
              <Card><div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" size={20} /> Loading billing...</div></Card>
            </>
          )
        }
        if (billingError) {
          return (
            <>
              <div className="st-section-header">
                <h3>Billing & Usage</h3>
              </div>
              <Card>
                <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <p style={{ color: '#ef4444' }}>{billingError}</p>
                  <button className="btn btn-sm btn-primary" onClick={fetchBilling}>Retry</button>
                </div>
              </Card>
            </>
          )
        }
        if (!billing) return null

        const serverPlan = billing.plan
        const subscription = billing.subscription
        const srvUsage = billing.usage || {}
        const currentPlanId = serverPlan?.id
        // Merge server plan (limits/price) with local cosmetic metadata
        const meta = PLANS.find(p => p.id === currentPlanId) || {}
        const currentPlan = {
          ...meta,
          id: currentPlanId,
          name: serverPlan?.name || meta.name || currentPlanId,
          price: (serverPlan?.price_cents ?? 0) / 100,
          currency: serverPlan?.currency || meta.currency || 'INR',
          color: meta.color || '#6366f1',
          limits: {
            contacts: serverPlan?.limits?.contacts,
            contactUploads: serverPlan?.limits?.contact_uploads,
            emails: serverPlan?.limits?.emails,
            aiEmails: serverPlan?.limits?.ai_emails,
            // Distinct from aiEmails above: ai_credits is the new, separate
            // credits pool that powers interactive AI features (campaign
            // creation, reports AI, insights chat, reply suggestions, etc).
            aiCredits: serverPlan?.limits?.ai_credits,
          },
        }

        const pick = (...keys) => {
          for (const k of keys) {
            const v = srvUsage?.[k]
            if (v != null) return Number(v) || 0
          }
          return 0
        }
        const usage = {
          emails: pick('emails_sent', 'emails', 'emails_used', 'email_count', 'sent_emails'),
          aiEmails: pick('ai_emails_used', 'ai_emails', 'ai_emails_sent', 'ai_email_count'),
          contacts: pick('contacts_active', 'contacts', 'active_contacts', 'contact_count'),
          contactUploads: pick('contacts_uploaded', 'contact_uploads', 'uploaded_contacts', 'contacts_upload_count'),
          // ai_credits is a distinct field from the ai_emails usage above —
          // use it directly rather than the multi-key `pick` fallback.
          aiCredits: Number(srvUsage?.ai_credits ?? 0) || 0,
        }
        // Debug: log once so you can see the raw shape the backend returned
        if (typeof window !== 'undefined' && !window.__billingUsageLogged) {
          console.log('[billing] raw usage payload:', srvUsage)
          window.__billingUsageLogged = true
        }

        const usageItems = [
          { key: 'emails', label: 'Emails Sent', icon: Send, used: usage.emails, limit: currentPlan.limits.emails, color: '#22c55e' },
          { key: 'aiEmails', label: 'AI Emails Used', icon: Brain, used: usage.aiEmails, limit: currentPlan.limits.aiEmails, color: '#8b5cf6' },
          { key: 'aiCredits', label: 'AI Credits', icon: Zap, used: usage.aiCredits, limit: currentPlan.limits.aiCredits, color: '#14b8a6' },
          { key: 'contacts', label: 'Active Contacts', icon: Users, used: usage.contacts, limit: currentPlan.limits.contacts, color: '#3b82f6' },
          { key: 'contactUploads', label: 'Contacts Uploaded', icon: Inbox, used: usage.contactUploads, limit: currentPlan.limits.contactUploads, color: '#f59e0b' },
        ]

        const periodEnd = subscription?.current_period_end
          ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
          : '—'
        const cancelScheduled = !!subscription?.cancel_at_period_end
        const warningResources = usageItems.filter(i => i.limit != null && pct(i.used, i.limit) >= 80)

        return (
          <>
            <div className="st-section-header">
              <h3>Billing & Usage</h3>
              <p>Track your plan, usage, and billing details</p>
            </div>

            {warningResources.length > 0 && (
              <div className="st-usage-warn-banner" style={{
                padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: 8,
                background: 'rgba(245, 158, 11, 0.1)', border: '1px solid #f59e0b',
                color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertTriangle size={16} />
                <span>You&apos;re approaching your limit on: {warningResources.map(w => w.label).join(', ')}. Consider upgrading.</span>
              </div>
            )}

            <Card title="Current Plan">
              <div className="st-plan">
                <div className="st-plan-left">
                  <div className="st-plan-icon" style={{ background: currentPlan.color }}>{currentPlan.name[0]}</div>
                  <div className="st-plan-details">
                    <h4>{currentPlan.name} Plan</h4>
                    <p className="st-plan-price">{formatMoney(currentPlan.price, currentPlan.currency)}<span>/month</span></p>
                    <p className="st-hint">
                      {cancelScheduled
                        ? `Cancels on ${periodEnd}. You keep access until then.`
                        : `Billed monthly. Next billing date: ${periodEnd}`}
                    </p>
                  </div>
                </div>
                <div className="st-plan-actions">
                  {cancelScheduled ? (
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={planActionLoading === currentPlanId}
                      onClick={() => handleSelectPlan(currentPlanId)}
                    >
                      {planActionLoading === currentPlanId ? <Loader2 size={14} className="spin" /> : 'Resubscribe'}
                    </button>
                  ) : (
                    <>
                      {currentPlanId !== 'scale' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setActiveSection('billing')}
                        >
                          Upgrade Plan
                        </button>
                      )}
                      {currentPlanId !== 'free' && (
                        <button className="btn btn-ghost btn-sm" onClick={handleCancelSubscription}>Cancel</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>

            <Card title="Usage This Period" description="Resets on your next billing date">
              <div className="st-usage-grid">
                {usageItems.map(item => {
                  const Icon = item.icon
                  const isUnlimited = item.limit === null
                  const percentage = pct(item.used, item.limit)
                  const isWarning = percentage >= 80 && percentage < 100
                  const isExceeded = percentage >= 100
                  return (
                    <div key={item.key} className="st-usage-item">
                      <div className="st-usage-top">
                        <span className="st-usage-label">
                          <Icon size={14} style={{ color: item.color, marginRight: 6, verticalAlign: 'middle' }} />
                          {item.label}
                        </span>
                        <span className="st-usage-val">
                          {fmt(item.used)}{isUnlimited ? '' : ` / ${fmt(item.limit)}`}
                        </span>
                      </div>
                      {isUnlimited ? (
                        <span className="st-usage-unlimited">Unlimited</span>
                      ) : (
                        <>
                          <div className="st-usage-bar">
                            <div className="st-usage-fill" style={{
                              width: `${percentage}%`,
                              background: isExceeded ? '#ef4444' : isWarning ? '#f59e0b' : item.color,
                            }} />
                          </div>
                          <div className="st-usage-meta">
                            <span className="st-hint">{percentage.toFixed(1)}% used</span>
                            {isWarning && <span className="st-usage-warn"><AlertTriangle size={12} /> Approaching limit</span>}
                            {isExceeded && <span className="st-usage-warn" style={{ color: '#ef4444' }}><AlertTriangle size={12} /> Limit reached</span>}
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </Card>

            <Card title="AI Credit Pricing" description="How AI credits are spent across features">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Credits</th>
                      <th>Unit</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditPricingLoading ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1.5rem' }}><Loader2 size={16} className="spin" /> Loading pricing...</td></tr>
                    ) : creditPricing.length === 0 ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>Pricing unavailable</td></tr>
                    ) : (
                      creditPricing.map((item) => {
                        const creditsStr = item.credits != null
                          ? item.credits
                          : (item.credits_min != null && item.credits_max != null
                            ? `${item.credits_min}–${item.credits_max}`
                            : '—')
                        return (
                          <tr key={item.key || item.label}>
                            <td><strong>{item.label}</strong></td>
                            <td>{creditsStr}</td>
                            <td>{item.unit}</td>
                            <td className="st-hint">{item.note || '—'}</td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card title="Available Plans" description="Compare and switch plans anytime">
              {plansLoading && !availablePlans.length && (
                <div style={{ padding: '0.5rem 0 1rem', color: '#94a3b8' }}>
                  <Loader2 size={14} className="spin" /> Loading plans...
                </div>
              )}
              <div className="st-plans-grid">
                {PLANS.map(plan => {
                  const isCurrent = plan.id === currentPlanId
                  return (
                    <div key={plan.id} className={`st-plan-card ${isCurrent ? 'st-plan-card--current' : ''} ${plan.recommended ? 'st-plan-card--recommended' : ''}`}>
                      {plan.recommended && <div className="st-plan-badge">Recommended</div>}
                      {isCurrent && <div className="st-plan-badge st-plan-badge--current">Current Plan</div>}
                      <div className="st-plan-card-header">
                        <h4 style={{ color: plan.color }}>{plan.name}</h4>
                        <p className="st-hint">{plan.tagline}</p>
                        <div className="st-plan-card-price">
                          <span className="st-plan-card-amount">{formatMoney(plan.price, plan.currency)}</span>
                          <span className="st-plan-card-period">/mo</span>
                        </div>
                      </div>
                      <ul className="st-plan-features">
                        {plan.features.map((f, i) => (
                          <li key={i}><Check size={14} style={{ color: plan.color }} /> {f}</li>
                        ))}
                      </ul>
                      <button
                        className={`btn btn-sm ${isCurrent ? 'btn-ghost' : 'btn-primary'}`}
                        disabled={isCurrent || planActionLoading === plan.id}
                        style={{ width: '100%' }}
                        onClick={() => !isCurrent && handleSelectPlan(plan.id)}
                      >
                        {isCurrent
                          ? 'Current Plan'
                          : planActionLoading === plan.id
                            ? <><Loader2 size={14} className="spin" /> Processing...</>
                            : `Switch to ${plan.name}`}
                      </button>
                    </div>
                  )
                })}
              </div>
            </Card>

            <Card title="Billing History" description="Recent invoices and payments">
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {invoicesLoading ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}><Loader2 size={16} className="spin" /> Loading invoices...</td></tr>
                    ) : invoices.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>No invoices yet</td></tr>
                    ) : (
                      invoices.map((inv) => {
                        const dateStr = inv.issued_at ? new Date(inv.issued_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : '—'
                        const amountStr = formatMoney((inv.amount_cents ?? 0) / 100, inv.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        return (
                          <tr key={inv.id}>
                            <td>{dateStr}</td>
                            <td>{inv.description || '—'}</td>
                            <td><strong>{amountStr}</strong></td>
                            <td><span className={`status-badge ${inv.status || ''}`}><span className="pulse-dot" />{inv.status || 'pending'}</span></td>
                            <td><button className="btn btn-ghost btn-sm" onClick={() => handleDownloadInvoice(inv.id)}>Download</button></td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )
      }

      case 'appearance':
        return (
          <>
            <div className="st-section-header">
              <h3>Appearance</h3>
              <p>Customize how Synxreach looks and feels</p>
            </div>

            <Card title="Theme" description="Choose your preferred color scheme">
              <div className="st-theme-options">
                {[{ icon: Moon, label: 'Dark', value: 'dark' }, { icon: Sun, label: 'Light', value: 'light' }, { icon: Monitor, label: 'System', value: 'system' }].map(t => (
                  <button key={t.value} className={`st-theme-btn ${theme === t.value ? 'st-theme-btn--active' : ''}`} onClick={() => setTheme(t.value)}>
                    <t.icon size={22} />
                    <span>{t.label}</span>
                    {theme === t.value && <Check size={14} className="st-theme-check" />}
                  </button>
                ))}
              </div>
            </Card>

            <Card title="Accent Color" description="Choose your preferred accent color">
              <div className="st-colors">
                {[
                  { color: '#6366f1', name: 'Indigo' }, { color: '#8b5cf6', name: 'Purple' },
                  { color: '#ec4899', name: 'Pink' }, { color: '#ef4444', name: 'Red' },
                  { color: '#f59e0b', name: 'Amber' }, { color: '#22c55e', name: 'Green' },
                  { color: '#3b82f6', name: 'Blue' }, { color: '#14b8a6', name: 'Teal' },
                ].map(c => (
                  <button key={c.color} className={`st-color-btn ${accentColor === c.color ? 'st-color-btn--active' : ''}`} onClick={() => setAccentColor(c.color)}>
                    <div className="st-color-circle" style={{ background: c.color }}>
                      {accentColor === c.color && <Check size={14} />}
                    </div>
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            </Card>

            <Card title="Density" description="Adjust the spacing and density of the interface">
              <div className="st-density-options">
                {[{ label: 'Compact', value: 'compact' }, { label: 'Comfortable', value: 'comfortable' }, { label: 'Spacious', value: 'spacious' }].map((d, i) => (
                  <button key={d.value} className={`st-density-btn ${density === d.value ? 'st-density-btn--active' : ''}`} onClick={() => setDensity(d.value)}>
                    <div className="st-density-preview">
                      {[...Array(4)].map((_, j) => <div key={j} className="st-density-bar" style={{ height: `${2 + i}px`, marginBottom: `${2 + i * 2}px` }} />)}
                    </div>
                    <span>{d.label}</span>
                    {density === d.value && <Check size={14} className="st-theme-check" />}
                  </button>
                ))}
              </div>
            </Card>
          </>
        )

      default:
        return (
          <>
            <div className="st-section-header">
              <h3>{settingsSections.find(s => s.id === activeSection)?.label}</h3>
              <p>This section is coming soon</p>
            </div>
            <div className="coming-soon">
              <div className="coming-soon-icon">🚧</div>
              <p>We&apos;re working on this feature. Check back soon!</p>
            </div>
          </>
        )
    }
  }

  return (
    <div className="page settings-page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Settings</h2>
          <p>Manage your account, preferences, and workspace</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave}
          disabled={notificationsSaving || preferencesSaving || sendingSaving || appearanceSaving}>
          {(notificationsSaving || preferencesSaving || sendingSaving || appearanceSaving)
            ? <><Loader2 size={18} className="spin" /><span>Saving...</span></>
            : saved ? <><Check size={18} /><span>Saved!</span></> : <><Save size={18} /><span>Save Changes</span></>}
        </button>
      </div>
      <div className="st-layout">
        <div className="st-nav">
          {settingsSections.map(section => {
            const isBilling = section.id === 'billing'
            const navPlanId = billing?.plan?.id
            const navPlanMeta = isBilling && navPlanId ? BILLING_PLANS.find(p => p.id === navPlanId) : null
            const billingPlan = isBilling && billing ? {
              name: billing.plan?.name || navPlanMeta?.name || navPlanId,
              color: navPlanMeta?.color || '#6366f1',
              limits: {
                emails: billing.plan?.limits?.emails,
                aiEmails: billing.plan?.limits?.ai_emails,
              },
            } : null
            const bu = billing?.usage || {}
            const emailsUsed = Number(bu.emails_sent ?? bu.emails ?? bu.emails_used ?? bu.sent_emails ?? 0) || 0
            const aiUsed = Number(bu.ai_emails_used ?? bu.ai_emails ?? bu.ai_emails_sent ?? bu.ai_credits_used ?? 0) || 0
            const emailsPct = billingPlan && billingPlan.limits.emails ? Math.min(100, (emailsUsed / billingPlan.limits.emails) * 100) : 0
            const aiPct = billingPlan && billingPlan.limits.aiEmails ? Math.min(100, (aiUsed / billingPlan.limits.aiEmails) * 100) : 0
            const maxPct = Math.max(emailsPct, aiPct)
            const barColor = maxPct >= 100 ? '#ef4444' : maxPct >= 80 ? '#f59e0b' : billingPlan?.color
            return (
              <button key={section.id}
                className={`st-nav-item ${activeSection === section.id ? 'st-nav-item--active' : ''}`}
                onClick={() => setActiveSection(section.id)}>
                <section.icon size={18} />
                <span>{section.label}</span>
                {isBilling && billingPlan && (
                  <span className="st-nav-billing">
                    <span className="st-nav-billing-plan" style={{ color: billingPlan.color }}>{billingPlan.name}</span>
                    <span className="st-nav-billing-bar">
                      <span className="st-nav-billing-fill" style={{ width: `${maxPct}%`, background: barColor }} />
                    </span>
                    <span className="st-nav-billing-pct">{maxPct.toFixed(0)}%</span>
                  </span>
                )}
                <ChevronRight size={14} className="st-nav-chevron" />
              </button>
            )
          })}
        </div>
        <div className="st-content">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

export default Settings
